"use client"

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react"

interface ViewportDimensions {
  width: number
  height: number
  gameWidth: number
  gameHeight: number
  pillarWidth: number
  isLandscape: boolean
  isMobile: boolean
  isDesktop: boolean
  scale: number
}

interface ViewportContextType extends ViewportDimensions {
  // Helper to get position relative to game area center
  getCenterX: () => number
  // Helper to check if we're in pillarbox mode
  isPillarboxed: boolean
}

const ViewportContext = createContext<ViewportContextType | null>(null)

// Game is designed for portrait (9:16 aspect ratio)
const TARGET_ASPECT_RATIO = 9 / 16
const MOBILE_BREAKPOINT = 768

export function ViewportProvider({ children }: { children: ReactNode }) {
  const [dimensions, setDimensions] = useState<ViewportDimensions>({
    width: typeof window !== "undefined" ? window.innerWidth : 768,
    height: typeof window !== "undefined" ? window.innerHeight : 1024,
    gameWidth: typeof window !== "undefined" ? window.innerWidth : 768,
    gameHeight: typeof window !== "undefined" ? window.innerHeight : 1024,
    pillarWidth: 0,
    isLandscape: false,
    isMobile: true,
    isDesktop: false,
    scale: 1,
  })

  const calculateDimensions = useCallback(() => {
    if (typeof window === "undefined") return

    const width = window.innerWidth
    const height = window.innerHeight
    const isLandscape = width > height
    const isMobile = width < MOBILE_BREAKPOINT || ("ontouchstart" in window && !isLandscape)
    const isDesktop = !isMobile

    let gameWidth = width
    const gameHeight = height
    let pillarWidth = 0

    // Apply pillarbox on desktop landscape
    if (isLandscape && isDesktop) {
      gameWidth = Math.floor(height * TARGET_ASPECT_RATIO)
      pillarWidth = Math.floor((width - gameWidth) / 2)
    }

    const scale = gameWidth / 768 // Base scale relative to 768px design width

    setDimensions({
      width,
      height,
      gameWidth,
      gameHeight,
      pillarWidth,
      isLandscape,
      isMobile,
      isDesktop,
      scale,
    })
  }, [])

  useEffect(() => {
    calculateDimensions()
    window.addEventListener("resize", calculateDimensions)
    window.addEventListener("orientationchange", calculateDimensions)
    return () => {
      window.removeEventListener("resize", calculateDimensions)
      window.removeEventListener("orientationchange", calculateDimensions)
    }
  }, [calculateDimensions])

  const getCenterX = useCallback(() => {
    return dimensions.gameWidth / 2
  }, [dimensions.gameWidth])

  const isPillarboxed = dimensions.isLandscape && dimensions.isDesktop

  return (
    <ViewportContext.Provider
      value={{
        ...dimensions,
        getCenterX,
        isPillarboxed,
      }}
    >
      {children}
    </ViewportContext.Provider>
  )
}

export function useViewport() {
  const context = useContext(ViewportContext)
  if (!context) {
    throw new Error("useViewport must be used within a ViewportProvider")
  }
  return context
}

// Optional hook for components that may be used outside provider
export function useViewportSafe() {
  return useContext(ViewportContext)
}
